import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { housingOwnerLabel, pickHousingForApplication } from '../../utils/applicationOwner';
import { H1, H2 } from '../../components/typography';
import { useToast } from '../../components/ui/ToastProvider';
import { FileText, Search, Filter, Clock, AlertCircle, Edit, ArrowRightLeft, Trash2 } from 'lucide-react';
import ApplicationDetailModal from '../../components/ApplicationDetailModal';
import EditApplicationModal from '../../components/modals/EditApplicationModal';
import ChangeStatusModal from '../../components/modals/ChangeStatusModal';
import DeleteApplicationModal from '../../components/modals/DeleteApplicationModal';

export default function HyriaApplications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editApplication, setEditApplication] = useState(null);
  const [statusChangeApp, setStatusChangeApp] = useState(null);
  const [deleteApplication, setDeleteApplication] = useState(null);
  const [searchParams] = useSearchParams();
  const toast = useToast();

  useEffect(() => {
    fetchAllApplications();
  }, []);

  // Auto-öppna ApplicationDetailModal när ?id=... finns i URL:en
  // (används från översikten för att djupklicka direkt in i en ansökan)
  useEffect(() => {
    const idParam = searchParams.get('id');
    if (idParam && applications.length > 0 && !modalOpen) {
      const target = applications.find(a => a.id === idParam);
      if (target) {
        setSelectedApplication(target);
        setModalOpen(true);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, applications]);

  const fetchAllApplications = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Hämta alla ansökningar från alla BRF:er
      const applicationsRef = collection(db, 'applications');
      const applicationsSnapshot = await getDocs(applicationsRef);
      
      const applicationsData = applicationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Hämta all housing-data för att kunna matcha ägarnamn
      const housingSnapshot = await getDocs(collection(db, 'housing'));
      const housingData = housingSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Hämta ägar- och föreningsinformation för alla ansökningar
      const enrichedApplications = await Promise.all(
        applicationsData.map(async (app) => {
          let ownerName = 'Okänd ägare';
          let associationName = 'Okänd BRF';

          // Först, kolla om ägare redan finns i ansökan (filtrera bort placeholder-värden)
          if (app.ownerFirstName && app.ownerLastName &&
              app.ownerFirstName !== 'Okänd' && app.ownerLastName !== 'Ägare') {
            ownerName = `${app.ownerFirstName} ${app.ownerLastName}`;
          } else if (app.createdBy) {
            // Om inte, hämta från users collection
            try {
              const ownerDoc = await getDoc(doc(db, 'users', app.createdBy));
              if (ownerDoc.exists()) {
                const ownerData = ownerDoc.data();
                ownerName = `${ownerData.firstName || ''} ${ownerData.lastName || ''}`.trim() || 'Okänd ägare';
              }
            } catch (error) {
            }
          }

          // Om fortfarande okänd, kolla housing member data
          if (ownerName === 'Okänd ägare') {
            const matchingHousing = pickHousingForApplication(
              housingData.filter(h => !app.associationId || h.associationId === app.associationId),
              app
            );
            ownerName = housingOwnerLabel(matchingHousing) || 'Okänd ägare';
          }

          // Hämta föreningsinformation
          if (app.associationId) {
            try {
              const assocDoc = await getDoc(doc(db, 'associations', app.associationId));
              if (assocDoc.exists()) {
                const assocData = assocDoc.data();
                associationName = assocData.name || 'Okänd BRF';
              }
            } catch (error) {
            }
          } else if (app.housingCooperative) {
            // Fallback till housingCooperative field om det finns
            associationName = app.housingCooperative;
          }

          return {
            ...app,
            ownerName,
            associationName
          };
        })
      );

      // Sortera efter senast skapade först
      enrichedApplications.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

      setApplications(enrichedApplications);
    } catch (err) {
      setError('Kunde inte hämta ansökningar');
      toast({ type: 'error', message: 'Kunde inte hämta ansökningar' });
    } finally {
      setLoading(false);
    }
  };

  // Beräkna process-info för varje ansökan (temporär logik tills vi har riktiga process-fält)
  // Arkiverade ansökningar (medlem borttagen) döljs som standard.
  const enrichedApplications = applications.filter(app => showArchived || !app.archivedAt).map(app => {
    const createdDate = new Date(app.createdAt || Date.now());
    const daysWaiting = Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
    
    // Temporär process-logik baserat på befintlig status
    let processStep = 1;
    let waitingFor = 'BRF';
    let actionRequired = 'BRF ska granska ansökan';
    
    switch (app.status) {
      case 'PENDING':
        processStep = 1;
        waitingFor = 'BRF';
        actionRequired = 'BRF ska granska och godkänna/avslå';
        break;
      case 'APPROVED':
        processStep = 2;
        waitingFor = 'OWNER';
        actionRequired = 'Ägare ska fylla i hyresgästuppgifter';
        break;
      case 'TENANT_INFO_SUBMITTED':
        processStep = 3;
        waitingFor = 'HYRIA';
        actionRequired = 'Hyria ska utföra kreditkontroll';
        break;
      case 'CREDIT_CHECK_COMPLETE':
        processStep = 4;
        waitingFor = 'BRF';
        actionRequired = 'BRF ska fatta slutgiltigt beslut';
        break;
      case 'FINAL_APPROVED':
      case 'ACTIVE':
        processStep = 5;
        waitingFor = 'NONE';
        actionRequired = 'Uthyrning godkänd';
        break;
      case 'AWAITING_OWNER_DECISION':
        processStep = 2;
        waitingFor = 'OWNER';
        actionRequired = 'Ägare ska godkänna avtalsvillkor';
        break;
      case 'ADDITIONAL_INFO':
        processStep = 1;
        waitingFor = 'OWNER';
        actionRequired = 'Ägare ska komplettera ansökan';
        break;
      case 'OWNER_REJECTED':
      case 'REJECTED':
      case 'FINAL_REJECTED':
        processStep = 0;
        waitingFor = 'NONE';
        actionRequired = 'Ansökan avvisad';
        break;
      default:
        processStep = 1;
        waitingFor = 'BRF';
        actionRequired = 'Okänd status';
    }

    return {
      ...app,
      processStep,
      waitingFor,
      actionRequired,
      daysWaiting,
      needsHyriaAction: waitingFor === 'HYRIA'
    };
  });

  // Filtrera ansökningar
  const filteredApplications = enrichedApplications.filter(app => {
    // Textfilter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        app.address?.toLowerCase().includes(search) ||
        app.apartmentNumber?.toLowerCase().includes(search) ||
        app.housingCooperative?.toLowerCase().includes(search) ||
        app.associationName?.toLowerCase().includes(search) ||
        app.ownerName?.toLowerCase().includes(search) ||
        app.tenantFirstName?.toLowerCase().includes(search) ||
        app.tenantLastName?.toLowerCase().includes(search) ||
        app.id.toLowerCase().includes(search);

      if (!matchesSearch) return false;
    }

    // Statusfilter
    const rejectedStatuses = ['REJECTED', 'FINAL_REJECTED', 'OWNER_REJECTED'];
    switch (filter) {
      case 'needs-action':
        return app.needsHyriaAction;
      case 'waiting-brf':
        return app.waitingFor === 'BRF';
      case 'waiting-owner':
        return app.waitingFor === 'OWNER';
      case 'urgent':
        return app.daysWaiting > 7;
      case 'approved':
        return app.status === 'FINAL_APPROVED' || app.status === 'ACTIVE';
      case 'rejected':
        return rejectedStatuses.includes(app.status);
      case 'all':
      default:
        return true;
    }
  });

  // Räkna olika kategorier
  const rejectedStatuses = ['REJECTED', 'FINAL_REJECTED', 'OWNER_REJECTED'];
  const counts = {
    all: applications.length,
    needsAction: enrichedApplications.filter(a => a.needsHyriaAction).length,
    waitingBrf: enrichedApplications.filter(a => a.waitingFor === 'BRF').length,
    waitingOwner: enrichedApplications.filter(a => a.waitingFor === 'OWNER').length,
    urgent: enrichedApplications.filter(a => a.daysWaiting > 7).length,
    approved: enrichedApplications.filter(a => a.status === 'FINAL_APPROVED' || a.status === 'ACTIVE').length,
    rejected: enrichedApplications.filter(a => rejectedStatuses.includes(a.status)).length
  };

  // Hantera klick på ansökan
  const handleApplicationClick = (application) => {
    setSelectedApplication(application);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedApplication(null);
  };

  const getStatusBadge = (app) => {
    const { status, processStep } = app;
    
    switch (status) {
      case 'PENDING':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">🟡 Väntar BRF ({processStep}/5)</span>;
      case 'APPROVED':
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">🔵 Väntar ägare ({processStep}/5)</span>;
      case 'TENANT_INFO_SUBMITTED':
        return <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">🟠 Kreditkontroll ({processStep}/5)</span>;
      case 'CREDIT_CHECK_COMPLETE':
        return <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">🟣 Slutbeslut ({processStep}/5)</span>;
      case 'FINAL_APPROVED':
      case 'ACTIVE':
        return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">🟢 Godkänd ({processStep}/5)</span>;
      case 'AWAITING_OWNER_DECISION':
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">📝 Väntar avtal ({processStep}/5)</span>;
      case 'ADDITIONAL_INFO':
        return <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">📋 Komplettering ({processStep}/5)</span>;
      case 'OWNER_REJECTED':
      case 'REJECTED':
      case 'FINAL_REJECTED':
        return <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">🔴 Avvisad</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">❓ Okänd</span>;
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-800 font-medium">Fel vid hämtning av ansökningar</span>
          </div>
          <p className="text-red-700 mt-1">{error}</p>
          <button 
            onClick={fetchAllApplications}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            Försök igen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <H1 className="flex items-center gap-2">
            <FileText className="w-6 h-6 text-blue-600" />
            Ansökningar
          </H1>
          <p className="text-gray-600 mt-1">Alla ansökningar från alla BRF:er</p>
        </div>
        
        <div className="text-sm text-gray-500">
          Visar {filteredApplications.length} av {applications.length} ansökningar
        </div>
      </div>

      {/* Filter-knappar */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            filter === 'all' 
              ? 'bg-blue-100 text-blue-800 border border-blue-200' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Alla ({counts.all})
        </button>
        
        <button
          onClick={() => setFilter('needs-action')}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            filter === 'needs-action' 
              ? 'bg-red-100 text-red-800 border border-red-200' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          🔴 Kräver åtgärd ({counts.needsAction})
        </button>
        
        <button
          onClick={() => setFilter('waiting-brf')}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            filter === 'waiting-brf' 
              ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          🟡 Väntar BRF ({counts.waitingBrf})
        </button>
        
        <button
          onClick={() => setFilter('waiting-owner')}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            filter === 'waiting-owner' 
              ? 'bg-blue-100 text-blue-800 border border-blue-200' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          🔵 Väntar ägare ({counts.waitingOwner})
        </button>
        
        <button
          onClick={() => setFilter('urgent')}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            filter === 'urgent'
              ? 'bg-orange-100 text-orange-800 border border-orange-200'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          ⏰ Brådskande ({counts.urgent})
        </button>

        <button
          onClick={() => setFilter('approved')}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            filter === 'approved'
              ? 'bg-green-100 text-green-800 border border-green-200'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          🟢 Godkända ({counts.approved})
        </button>

        <button
          onClick={() => setFilter('rejected')}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            filter === 'rejected'
              ? 'bg-red-100 text-red-800 border border-red-200'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          🔴 Avvisade ({counts.rejected})
        </button>
      </div>

      {/* Sök */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <label className="order-2 flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="rounded border-gray-300"
          />
          Visa arkiverade
        </label>
        <div className="relative max-w-md flex-1 order-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Sök ansökan (adress, BRF, lägenhet, hyresgäst, ID...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
          />
        </div>
      </div>

      {/* Ansökningstabell */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        {filteredApplications.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Inga ansökningar hittades</h3>
            <p className="text-gray-500">
              {searchTerm ? 'Försök med andra söktermer' : 'Inga ansökningar matchar det valda filtret'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ägare
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hyresgäst
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    BRF
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lägenhet
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Väntetid
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Åtgärder
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredApplications.map((app) => (
                  <tr 
                    key={app.id} 
                    className="hover:bg-gray-50 cursor-pointer transition"
                    onClick={() => handleApplicationClick(app)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="font-medium text-gray-900">
                          {app.ownerName || 'Okänd ägare'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {app.address}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {app.tenantFirstName || app.tenantLastName ? (
                        <div>
                          <div className="font-medium text-gray-900">
                            {`${app.tenantFirstName || ''} ${app.tenantLastName || ''}`.trim()}
                          </div>
                          {app.tenantEmail && (
                            <div className="text-sm text-gray-500">{app.tenantEmail}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 italic">Ej inlagd</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {app.associationName || app.housingCooperative || 'Okänd BRF'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div>
                        <div className="font-medium text-gray-900">
                          🏠 Lägenhet {app.apartmentNumber}
                        </div>
                        <div className="text-sm text-gray-500">
                          #{app.id.slice(-6)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(app)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`font-medium ${
                        app.daysWaiting > 7 ? 'text-red-600' :
                        app.daysWaiting > 3 ? 'text-yellow-600' : 'text-gray-600'
                      }`}>
                        {app.daysWaiting} dagar
                      </span>
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => setEditApplication(app)}
                          className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition"
                          title="Redigera ansökan"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setStatusChangeApp(app)}
                          className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition"
                          title="Ändra status manuellt"
                        >
                          <ArrowRightLeft className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteApplication(app)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
                          title="Ta bort ansökan permanent"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer info */}
      {filteredApplications.length > 0 && (
        <div className="mt-4 text-sm text-gray-500 text-center">
          Visar {filteredApplications.length} av {applications.length} ansökningar
          {searchTerm && ` för "${searchTerm}"`}
        </div>
      )}

      {/* Detail Modal */}
      <ApplicationDetailModal
        application={selectedApplication}
        isOpen={modalOpen}
        onClose={handleCloseModal}
      />

      {/* Edit Modal */}
      <EditApplicationModal
        open={!!editApplication}
        application={editApplication}
        onClose={() => setEditApplication(null)}
        onSuccess={() => {
          setEditApplication(null);
          fetchAllApplications();
        }}
      />

      {/* Change Status Modal */}
      <ChangeStatusModal
        open={!!statusChangeApp}
        application={statusChangeApp}
        onClose={() => setStatusChangeApp(null)}
        onSuccess={() => {
          setStatusChangeApp(null);
          fetchAllApplications();
        }}
      />

      {/* Delete Modal */}
      <DeleteApplicationModal
        open={!!deleteApplication}
        application={deleteApplication}
        onClose={() => setDeleteApplication(null)}
        onSuccess={() => {
          setDeleteApplication(null);
          fetchAllApplications();
        }}
      />
    </div>
  );
}
